// Traditional pip positions on a 100×140 face (viewBox units). Pips whose
// y is past the vertical centre are rendered upside-down, exactly like a real
// deck. Coordinates chosen to match a standard Bicycle-style layout.
const PIP_LAYOUT = {
  '2': [[50, 26], [50, 114]],
  '3': [[50, 26], [50, 70], [50, 114]],
  '4': [[33, 30], [67, 30], [33, 110], [67, 110]],
  '5': [[33, 30], [67, 30], [50, 70], [33, 110], [67, 110]],
  '6': [[33, 30], [67, 30], [33, 70], [67, 70], [33, 110], [67, 110]],
  '7': [[33, 30], [67, 30], [50, 50], [33, 70], [67, 70], [33, 110], [67, 110]],
  '8': [[33, 30], [67, 30], [50, 50], [33, 70], [67, 70], [50, 90], [33, 110], [67, 110]],
  '9': [[33, 30], [67, 30], [33, 58], [67, 58], [50, 70], [33, 82], [67, 82], [33, 110], [67, 110]],
  '10':[[33, 30], [67, 30], [50, 42], [33, 58], [67, 58], [33, 82], [67, 82], [50, 98], [33, 110], [67, 110]],
};

const FACE_CARDS = new Set(['J', 'Q', 'K']);

// Classic two-colour suits, matching the illustrated court deck (Buck's
// source sheet): black spades/clubs, red hearts/diamonds. The SVG silhouettes
// keep spade vs club unmistakable by shape.
const SUIT_COLORS = { S: '#1a1a1a', C: '#1a1a1a', H: '#d21f26', D: '#d21f26' };

// Crisp suit silhouettes drawn in a 100×100 box (font glyphs for ♠/♣ blur
// together at card sizes; the club's three round lobes vs the spade's single
// pointed leaf read clearly as paths).
const SUIT_PATHS = {
  S: 'M50 4 C38 28 13 38 13 57 C13 71 26 78 39 72 C36 82 30 89 23 96 L77 96 C70 89 64 82 61 72 C74 78 87 71 87 57 C87 38 62 28 50 4 Z',
  C: 'M50 6 a19 19 0 1 1 -14 32 a19 19 0 1 1 -6 -0.5 a19 19 0 1 1 40 0 a19 19 0 1 1 -6 0.5 A19 19 0 1 1 50 6 M46 48 C46 68 40 82 32 94 L68 94 C60 82 54 68 54 48 Z',
  H: 'M50 92 C22 66 9 50 9 32 C9 17 20 8 31 8 C40 8 47 13 50 21 C53 13 60 8 69 8 C80 8 91 17 91 32 C91 50 78 66 50 92 Z',
  D: 'M50 4 L86 50 L50 96 L14 50 Z',
};

/** A suit glyph centred at (x, y), `size` units tall, as a crisp SVG path. */
function SuitGlyph({ suit, x, y, size, fill }) {
  const s = size / 100;
  return (
    <path
      d={SUIT_PATHS[suit] || SUIT_PATHS.S}
      fill={fill || SUIT_COLORS[suit]}
      transform={`translate(${x - size / 2} ${y - size / 2}) scale(${s})`}
    />
  );
}

export default function CardFace({ card, size = 'normal' }) {
  const rank = card.rank;
  const suit = card.suit;
  const fill = SUIT_COLORS[suit] || SUIT_COLORS.S;

  // Small cards (player seats, showdown reveal, replay) can't legibly show
  // pip/court detail — especially on phones — so use a clean rank-over-suit
  // face, like a simplified index card.
  if (size === 'small') {
    return (
      <svg className="card__face-svg" viewBox="0 0 100 140" preserveAspectRatio="xMidYMid meet">
        <text x="50" y="42" fontSize="40" fontWeight="700" fill={fill} textAnchor="middle"
              dominantBaseline="central" fontFamily="Georgia, 'Times New Roman', serif">{rank}</text>
        <SuitGlyph suit={suit} x={50} y={98} size={44} fill={fill} />
      </svg>
    );
  }

  // Court cards are Buck's complete painted artworks — full figure and the
  // card's own painted indices. Render the whole card contained (meet) so
  // nothing is ever cropped; the artwork's cream blends with the card ground.
  // No drawn chips (the art carries its own indices). Small courts fall
  // through to the simple face above.
  if (FACE_CARDS.has(rank)) {
    const base = process.env.PUBLIC_URL || '';
    return (
      <svg className="card__face-svg" viewBox="0 0 100 140" preserveAspectRatio="xMidYMid meet">
        <image
          href={`${base}/cards/court/${rank}${suit}.png`}
          x="0" y="0" width="100" height="140"
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    );
  }

  const rankFont = rank === '10' ? 15 : 18;

  // Corner index: rank stacked over a small suit glyph. Rendered twice — the
  // second rotated 180° into the opposite corner.
  const corner = (
    <g>
      <text x="0" y="15" fontSize={rankFont} fontWeight="700" fill={fill}
            textAnchor="middle" fontFamily="Georgia, 'Times New Roman', serif">{rank}</text>
      <SuitGlyph suit={suit} x={0} y={26} size={12} fill={fill} />
    </g>
  );

  let center;
  if (rank === 'A') center = <AceCenter suit={suit} fill={fill} />;
  else center = (PIP_LAYOUT[rank] || []).map(([x, y], i) => (
    <g key={i} transform={y > 70 ? `rotate(180 ${x} ${y})` : undefined}>
      <SuitGlyph suit={suit} x={x} y={y} size={19} fill={fill} />
    </g>
  ));

  return (
    <svg className="card__face-svg" viewBox="0 0 100 140" preserveAspectRatio="xMidYMid meet">
      <g transform="translate(11 6)">{corner}</g>
      <g transform="translate(89 134) rotate(180)">{corner}</g>
      {center}
    </svg>
  );
}

// Ace: a single large ornate pip, ringed like a real Ace.
function AceCenter({ suit, fill }) {
  return (
    <g>
      <circle cx="50" cy="72" r="27" fill="none" stroke={fill} strokeWidth="1" opacity="0.25" />
      <circle cx="50" cy="72" r="22" fill="none" stroke={fill} strokeWidth="0.6" opacity="0.4" />
      <SuitGlyph suit={suit} x={50} y={72} size={34} fill={fill} />
    </g>
  );
}
